import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Loader2, ExternalLink, Shield, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminSSOTester() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [returnTo, setReturnTo] = useState("/dashboard");
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [error, setError] = useState("");
  const [lastUrl, setLastUrl] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/sso/clients`);
        const data = await res.json();
        setClients(data?.clients || []);
      } catch (e) {
        setError("Failed to load OIDC clients");
      } finally {
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, []);

  const handleClientChange = (value) => {
    setClientId(value);
    const client = clients.find(c => c.client_id === value);
    setSelectedClient(client);
  };

  const runTest = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_URL}/api/admin/sso/test-link?client_id=${encodeURIComponent(clientId)}&return_to=${encodeURIComponent(returnTo)}`
      );
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Failed to generate test link");
      }
      
      const url = data?.url;
      if (!url) throw new Error("No URL returned");
      
      setLastUrl(url);
      toast.success("Starting SSO flow...");
      
      // Small delay so user sees the toast
      setTimeout(() => {
        window.location.href = url;
      }, 500);
    } catch (e) {
      setError(e.message || "Failed to generate SSO test link");
      toast.error("SSO test failed");
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = () => {
    if (lastUrl) {
      navigator.clipboard.writeText(lastUrl);
      toast.success("URL copied to clipboard");
    }
  };

  return (
    <Card data-testid="admin-sso-tester">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-lg">Test SSO Flow</CardTitle>
        </div>
        <CardDescription>
          Verify OIDC integration before deploying to production apps
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadingClients ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="client-select">OIDC Client</Label>
              <Select value={clientId} onValueChange={handleClientChange}>
                <SelectTrigger id="client-select" data-testid="sso-client-select">
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>
                      {c.client_name || c.client_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedClient && (
              <div className="rounded-lg bg-slate-50 p-3 text-xs space-y-1">
                <div className="font-medium text-slate-700">Redirect URIs:</div>
                {selectedClient.redirect_uris?.map((uri, i) => (
                  <div key={i} className="text-slate-500 truncate" title={uri}>
                    {uri}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="return-to">Return To Path</Label>
              <Input
                id="return-to"
                value={returnTo}
                onChange={(e) => setReturnTo(e.target.value)}
                placeholder="/dashboard"
                data-testid="sso-return-to-input"
              />
              <p className="text-xs text-slate-500">
                Where the user should land after successful authentication
              </p>
            </div>

            <Button
              onClick={runTest}
              disabled={!clientId || loading}
              className="w-full"
              data-testid="sso-test-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting SSO...
                </>
              ) : (
                <>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Test SSO Flow
                </>
              )}
            </Button>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {lastUrl && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Test URL Generated
                </div>
                <div 
                  className="text-xs text-slate-500 break-all bg-slate-50 p-2 rounded cursor-pointer hover:bg-slate-100"
                  onClick={copyUrl}
                  title="Click to copy"
                >
                  {lastUrl}
                </div>
                <p className="text-xs text-slate-400">Click URL to copy</p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default AdminSSOTester;
